import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { gt } from 'ember-truth-helpers';
import type SkinnyLocation from 'garaje/models/skinny-location';
import type LocalStorageService from 'garaje/services/local-storage';
import type WindowLocationService from 'garaje/services/window-location';
import { loadGoogleMaps } from 'garaje/utils/script-loader';

interface LocationsMapComponentSignature {
  Args: {
    locations: SkinnyLocation[];
  };
  Element: HTMLDivElement;
}

export default class LocationsMap extends Component<LocationsMapComponentSignature> {
  @service declare localStorage: LocalStorageService;
  @service declare windowLocation: WindowLocationService;

  @tracked map?: google.maps.Map;
  @tracked markers: google.maps.Marker[] = [];

  get locationsWithPosition(): SkinnyLocation[] {
    return this.args.locations.filter((location) => location.latitude && location.longitude);
  }

  @action
  insert(element: HTMLDivElement): void {
    void this._insertMap(element);
  }

  @action
  update(element: HTMLDivElement): void {
    this._removeMarkerListeners(this.markers);
    void this._insertMap(element);
  }

  @action
  doDestroy(): void {
    this._removeMarkerListeners(this.markers);
  }

  async _insertMap(element: HTMLDivElement): Promise<void> {
    if (this.locationsWithPosition.length < 1) {
      return;
    }
    await loadGoogleMaps();
    if (!window.google || !window.google.maps) {
      return;
    }
    const container = <HTMLCanvasElement>element.getElementsByClassName('locations-map-canvas')[0];
    const options = {
      disableDefaultUI: true,
      draggable: true,
      scrollwheel: true,
      disableDoubleClickZoom: true,
    };

    const map = new window.google.maps.Map(container, options);
    const markers: google.maps.Marker[] = [];
    this.locationsWithPosition.forEach((location) => {
      const infowindow = new google.maps.InfoWindow({ content: location.nameWithCompanyName });
      const url = `https://envoy-public-assets.s3.amazonaws.com/icons/map-pin-${
        location.disabled ? 'grey' : 'red'
      }.png`;
      const marker = new window.google.maps.Marker({
        position: new window.google.maps.LatLng(Number(location.latitude), Number(location.longitude)),
        map,
        icon: { url, scaledSize: new window.google.maps.Size(22, 31) },
      });
      window.google.maps.event.addListener(marker, 'mouseover', () => infowindow.open(map, marker));
      window.google.maps.event.addListener(marker, 'mouseout', () => infowindow.close());
      window.google.maps.event.addListener(marker, 'click', () => {
        this.localStorage.setItem('current_location', location.id);
        this.windowLocation.assign('/');
      });
      markers.push(marker);
    });
    map.fitBounds(this._generateBounds(markers));
    this.map = map;
    this.markers = markers;
  }

  /**
   * Center/Set Zoom of `map` to cover all visible Markers (locations)
   * @param {Array<google.maps.Marker>}   markers
   */
  _generateBounds(markers: google.maps.Marker[]): google.maps.LatLngBounds {
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((marker) => bounds.extend(marker.getPosition()!));
    return bounds;
  }

  /**
   * Clears all listeners added to the markers
   */
  _removeMarkerListeners(markers: google.maps.Marker[]): void {
    if (!window.google || !window.google.maps.event.clearListeners) {
      return;
    }
    markers.forEach((marker) => {
      window.google.maps.event.clearListeners(marker, 'mouseover');
      window.google.maps.event.clearListeners(marker, 'mouseout');
      window.google.maps.event.clearListeners(marker, 'click');
    });
  }

  <template>
    <div {{didInsert this.insert}} {{didUpdate this.update}} {{willDestroy this.doDestroy}}>
      {{#if (gt this.locationsWithPosition.length 0)}}
        <div data-test-map class="locations-map-canvas rounded" style="width:100%;height:320px"></div>
      {{else}}
        <div data-test-map-placeholder class="googleMapPlaceholder icon-mapPin flex items-center" style="height:255px">
          <p class="flex-auto mb-0 text-carbon-50 text-sm text-center mt-10">No locations with valid addresses available</p>
        </div>
      {{/if}}
    </div>
  </template>
}
